import { css } from '@emotion/core';
import { gray1, gray3 } from 'styles/global_defaults/colors';
import { halfSpacing } from 'styles/global_defaults/scaffolding';
import React, { useState, useEffect, useRef, FunctionComponent } from 'react';
import useClickOutside from 'shared/hooks/use-click-outside';
import NvPopover, { NvPopoverProps } from './nv-popover';
import NvTooltip from './nv-tooltip';


export type BaseButtonProps = {
  onClick?: () => void,
  tooltip?: string,
  disabled?: boolean,
  /** A function component that will wrap the button, if present.
   * I don't know that this is a good design but it greatly simplifies my implementation for the file upload button in Image Lecture Component. I'll
   * revisit this later. */
  wrappingComponent?: FunctionComponent
  wrappingComponentProps?: any,
};

export type TextButton = {
  type: 'text',
  text: string,
} & BaseButtonProps;

export type IconButton = {
  type: 'icon',
  /** The classname of the icon to show */
  iconClass: string,
} & BaseButtonProps;

export type Button =
  TextButton |
  IconButton;

type NvButtonPopoverProps = {
  buttons: Button[],
  setShow?: (show: boolean) => void,
};

/** This is a helper function I'm trying out to make conditionally wrapping components easier - Nathan */
const ConditionalWrap = ({ condition, wrap, children }) => (<React.Fragment>{condition ? wrap(children) : children}</React.Fragment>);

export const NvButtonPopover = (props: Omit<NvPopoverProps, 'content'> & NvButtonPopoverProps) => {
  const ref = useRef(null);

  useClickOutside(ref, () => {
    props?.setShow(false);
  });

  const popoverStyles = css`
    max-width: none;
    background-color: ${gray1};
    height: 36px;
    border: 0;

    .bs4-popover-body {
      color: white;
      padding: 0;
      /* Clips the edges of the buttons on the sides of the popover */
      overflow: hidden;
    }

    .arrow::after {
      border-top-color: ${gray1};
    }
  `;

  const styles = css`
    .popover-button {
      /* min-width: 48px; */
      height: 36px;
      padding-left: 16px;
      padding-right: 16px;
      padding-top: ${halfSpacing}px;
      padding-bottom: ${halfSpacing}px;
      &:not(.disabled):hover {
        cursor: pointer;
        background-color: ${gray3};
      }

      &.disabled {
        color: ${gray3};
      }
    }
  `;

  const [content, setContent] = useState(null);


  useEffect(() => {
    setContent(
      <div ref={ref} css={styles} className='d-flex'>
        {props.buttons.map(button => (
          <div key={`button-${keyForButton(button)}-container`}>
            {/* The NvTooltip above mysteriously does not render if I don't place a div here */ }
            <ConditionalWrap
              condition={!!button.wrappingComponent}
              wrap={(children) => {
                const WrappingComponent = button.wrappingComponent;

                return <WrappingComponent {...button.wrappingComponentProps}>{children}</WrappingComponent>;
              }}
            >
              <NvTooltip
                text={button.tooltip}
                enabled={!!button.tooltip && !button.disabled}
                key={`${keyForButton(button)}-tooltip`}
                placement='bottom'
                // These float outside their container and will be hidden via the `[data-popper-escaped=true]` selector in
                // react-app-component-styles.scss unless this is false
                preventOverflow={false}
              >
                <div
                  key={keyForButton(button)}
                  className={`popover-button ${button.disabled ? 'disabled' : ''}`}
                  onClick={() => !button.disabled && button.onClick?.()}
                >
                  {button.type === 'icon' && <div className={`icon icon-smallest ${button.iconClass}`} />}
                  {button.type === 'text' && <div>{button.text}</div>}
                </div>
              </NvTooltip>
            </ConditionalWrap>
          </div>
        ))}
      </div>,
    );
  }, [props.buttons]);

  return (
    <NvPopover
      content={content}
      {...props}
      overlayStyles={popoverStyles}
    />
  );
};

const keyForButton = (button: Button): string => {
  switch (button.type) {
    case 'icon':
      return button.iconClass;
    case 'text':
      return button.text;
    default:
      return '';
  }
};

export default NvButtonPopover;
